import { useState } from 'react';
import { Card, CardHeader } from '../../../../../components/ui/Card';
import {
  useVisibilityBrands,
  useUpsertVisibilityBrand,
  useSeedVisibility,
} from '../../../../../hooks/useVisibility';

export function BrandAdmin() {
  const { data } = useVisibilityBrands();
  const upsert = useUpsertVisibilityBrand();
  const seed = useSeedVisibility();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [domains, setDomains] = useState('');
  const [aliases, setAliases] = useState('');
  const [isOwn, setIsOwn] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function runSeed() {
    const empty = (data?.items.length ?? 0) === 0;
    const confirmed =
      empty ||
      window.confirm(
        'Re-seed visibility?\n\nInserts any missing default brands (Sequesto + 11 competitors), engines, intent tags, topic clusters, and probe queries. Existing rows are left alone. Safe to re-run.',
      );
    if (!confirmed) return;
    await seed.mutateAsync();
  }

  const reset = () => {
    setName(''); setDomains(''); setAliases(''); setIsOwn(false); setIsActive(true); setEditingId(null);
  };

  function loadForEdit(brand: { id: string; name: string; domains: string[]; aliases: string[]; isOwn: boolean; isActive: boolean }) {
    setEditingId(brand.id);
    setName(brand.name);
    setDomains(brand.domains.join(', '));
    setAliases(brand.aliases.join(', '));
    setIsOwn(brand.isOwn);
    setIsActive(brand.isActive);
    setOpen(true);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    await upsert.mutateAsync({
      name: name.trim(),
      domains: domains.split(',').map((s) => s.trim()).filter(Boolean),
      aliases: aliases.split(',').map((s) => s.trim()).filter(Boolean),
      isOwn,
      isActive,
    });
    reset();
    setOpen(false);
  }

  return (
    <Card padding="md">
      <CardHeader
        title="Tracked brands"
        description={`${data?.items.length ?? 0} brands tracked — pick which competitors to measure against.`}
        action={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={runSeed}
              disabled={seed.isPending}
              title="Insert any missing default brands, engines, tags, clusters, and probes. Idempotent."
              className="text-body-xs font-medium text-text-tertiary hover:text-text-primary underline-offset-2 hover:underline disabled:opacity-50"
            >
              {seed.isPending
                ? 'Seeding…'
                : (data?.items.length ?? 0) === 0
                  ? 'Seed visibility'
                  : 'Re-seed defaults'}
            </button>
            <button
              type="button"
              onClick={() => { reset(); setOpen((v) => !v); }}
              className="text-body-xs font-medium text-text-tertiary hover:text-text-primary underline-offset-2 hover:underline"
            >
              {open ? 'Cancel' : '+ Add / edit brand'}
            </button>
          </div>
        }
      />
      {seed.isSuccess && seed.data && (
        <p className="mb-3 text-body-xs text-success">
          Seeded — added {seed.data.brands} brand(s), {seed.data.engines} engine(s),{' '}
          {seed.data.tags} tag(s), {seed.data.probesInserted} probe(s),{' '}
          {seed.data.clustersCreated} cluster(s). Try Run Citation Check now.
        </p>
      )}
      {seed.isError && (
        <p className="mb-3 text-body-xs text-error">
          Seed failed: {seed.error instanceof Error ? seed.error.message : 'unknown error'}
        </p>
      )}
      {data && data.items.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {data.items.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => loadForEdit(b)}
              className={`text-body-xs rounded-full px-2.5 py-1 border ${b.isOwn ? 'border-text-primary bg-info-light/40 text-text-primary' : 'border-border-light text-text-tertiary hover:text-text-primary'}`}
            >
              {b.name}{b.isOwn ? ' (You)' : ''}{!b.isActive ? ' · paused' : ''}
            </button>
          ))}
        </div>
      )}
      {open && (
        <form onSubmit={submit} className="border border-border-light rounded-lg p-3 space-y-2 bg-surface-secondary/40">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-body-sm">
            <label className="flex flex-col gap-1">
              <span className="text-body-xs text-text-tertiary">Name</span>
              <input className="rounded border border-border-light bg-surface-primary px-2 py-1" value={name} onChange={(e) => setName(e.target.value)} required />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-body-xs text-text-tertiary">Domains (comma-separated)</span>
              <input className="rounded border border-border-light bg-surface-primary px-2 py-1" value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="example.com, example.io" />
            </label>
            <label className="flex flex-col gap-1 md:col-span-2">
              <span className="text-body-xs text-text-tertiary">Aliases (comma-separated)</span>
              <input className="rounded border border-border-light bg-surface-primary px-2 py-1" value={aliases} onChange={(e) => setAliases(e.target.value)} placeholder="Acme Co, ACME" />
            </label>
          </div>
          <div className="flex items-center gap-4 text-body-sm">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={isOwn} onChange={(e) => setIsOwn(e.target.checked)} />Own brand</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />Active</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { reset(); setOpen(false); }} className="text-body-xs text-text-tertiary px-2 py-1">Cancel</button>
            <button type="submit" disabled={upsert.isPending || !name.trim()} className="rounded bg-text-primary px-3 py-1 text-body-xs text-text-inverse disabled:opacity-50">
              {upsert.isPending ? 'Saving…' : editingId ? 'Update brand' : 'Add brand'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}
